import { CARD_MAX_LEVEL, type CardId, cardCooldownMsAtLevel, cardDefinition } from './cards';

/**
 * **O que cada carta faz, em uma frase** (gate V28H.G).
 *
 * ## Porque vive aqui e não na imagem
 *
 * Porque um texto gravado num PNG é um texto que não se traduz, não se lê com
 * um leitor de ecrã, não se corrige sem reexportar dezasseis ficheiros e não
 * existe enquanto a arte não existir. A descrição é conteúdo, e conteúdo vive
 * em código onde pessoas o decidem.
 *
 * ## Porque é um `Record<CardId, string>` e não um mapa
 *
 * Porque assim o COMPILADOR exige as dezasseis. Um id novo em `cards.ts` sem
 * descrição aqui é erro de compilação - não uma carta que chega ao ecrã com uma
 * legenda vazia e ninguém repara.
 *
 * ## O que estas frases deliberadamente NÃO dizem
 *
 * Números que ainda não existem. Nenhuma delas promete uma percentagem, um raio
 * ou um dano: as mecânicas não estão implementadas, e escrever "+40% de
 * velocidade" hoje seria escrever uma decisão de equilíbrio numa legenda.
 *
 * As três excepções são as que o contrato aprovado FIXOU: os treze segundos do
 * Leviatã, os nove do Manto e os dois canhões dos Super Canhões. Essas são
 * decisões fechadas, e omiti-las seria esconder o que a carta é.
 */
export const CARD_DESCRIPTIONS: Readonly<Record<CardId, string>> = Object.freeze({
  card_dash: 'Avança rapidamente na direção para a qual a nave aponta.',
  card_speed_burst: 'Aumenta temporariamente a velocidade de movimento.',
  card_solar_lance:
    'Carrega um laser que cresce em espessura e dano. Durante a carga, a nave pode mirar, mas não se mover.',
  card_charged_fireball: 'Carrega e lança uma grande esfera de fogo explosiva.',
  card_leviathan:
    'Invoca um veículo aéreo com duas entradas e seis canhões. Qualquer nave próxima pode pilotá-lo por 13 segundos.',
  card_blade_storm:
    'Cria uma lâmina neon gigante que gira ao redor da nave e atinge inimigos próximos.',
  card_catole: 'Lança uma Bomba Atômica teleguiada colossal, com grande explosão, dano e empurrão.',
  card_attack_drone:
    'Invoca um drone que segue o dono, caça inimigos, dispara e pode colidir em ataque kamikaze.',
  card_malware: 'Aplica a todos os inimigos um bloqueio que reduz a cura recebida.',
  card_freeze: 'Lança uma esfera de gelo que paralisa o alvo e pode também impedir seus ataques.',
  card_slow:
    'Lança uma estrela de gelo que reduz temporariamente a velocidade de movimento do alvo.',
  card_attack_break:
    'Dispara energia elétrica que reduz temporariamente a velocidade de ataque do alvo.',
  card_damage_break: 'Marca o inimigo e reduz temporariamente o dano que ele causa.',
  card_cloak: 'Torna a nave invisível por 9 segundos. Atacar encerra o efeito.',
  card_bastion: 'Torna a nave temporariamente inalvejável.',
  card_super_cannons:
    'Invoca dois canhões enormes que disparam na mesma velocidade de ataque da nave.',
});

/**
 * A descrição desta carta, ou `null` para um id que o catálogo não conhece.
 *
 * `null` e não uma excepção, pela mesma razão que `cardDefinition` devolve
 * `null`: a interface desenha o que o servidor lhe deu, e uma carta que este
 * build não conhece não deve derrubar um painel.
 */
export function cardDescription(cardId: string): string | null {
  return (CARD_DESCRIPTIONS as Record<string, string | undefined>)[cardId] ?? null;
}

/**
 * O cooldown desta carta NESTE nível, em milissegundos.
 *
 * Uma conveniência sobre `cardCooldownMsAtLevel`, para quem tem um id e um nível
 * e não a definição. `null` para um id desconhecido - nunca um zero, que se
 * leria como "sem espera".
 */
export function cardCooldownMsOf(cardId: string, level: number): number | null {
  const card = cardDefinition(cardId);
  if (!card) {
    return null;
  }
  return cardCooldownMsAtLevel(card.baseCooldownMs, Math.min(level, CARD_MAX_LEVEL));
}
